import { useEffect, useState, useCallback } from "react"
import { useAuthState } from "@/hooks/useAuth"
import { QUERY_COLLECTIONS_ENDPOINT } from "@/config/endpoints"
import { db } from "@/lib/firebase"
import { doc, getDoc } from "firebase/firestore"
import type { PortfolioItem } from "@/types/models"

interface TattooItemRef {
  type: "flash" | "portfolioItem"
  id: string
}

interface QueryCollectionsResponse {
  left: TattooItemRef[]
  right: TattooItemRef[]
}

const ITEMS_PER_LOAD = 40

/**
 * Hook to query collection items from the backend using the query_collections endpoint
 */
export function useQueryCollectionItems(collectionId: string | undefined) {
  const { user } = useAuthState()
  const [allLeftItems, setAllLeftItems] = useState<TattooItemRef[]>([])
  const [allRightItems, setAllRightItems] = useState<TattooItemRef[]>([])
  const [loadedItems, setLoadedItems] = useState<PortfolioItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [currentLoadedCount, setCurrentLoadedCount] = useState(0)
  const [activeRequestId, setActiveRequestId] = useState<number>(0)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  
  const currentRequestIdRef = useState(() => ({ current: 0 }))[0]

  // Fetch IDs from backend when collectionId or refetchTrigger changes
  useEffect(() => {
    if (!collectionId) {
      setLoading(true)
      setInitialLoadComplete(false)
      return
    }

    const fetchIds = async () => {
      currentRequestIdRef.current += 1
      const thisRequestId = currentRequestIdRef.current
      
      try {
        setLoading(true)
        setError(null)
        setLoadedItems([])
        setCurrentLoadedCount(0)
        setInitialLoadComplete(false)

        console.log("[useQueryCollectionItems] Fetching IDs for collection:", collectionId, "requestId:", thisRequestId)

        const response = await fetch(QUERY_COLLECTIONS_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            collectionId,
            userId: user?.uid || null,
          }),
        })

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}: ${response.statusText}`)
        }

        const data: QueryCollectionsResponse = await response.json()
        
        if (thisRequestId !== currentRequestIdRef.current) {
          console.log("[useQueryCollectionItems] Ignoring stale response for requestId:", thisRequestId)
          return
        }
        
        console.log("[useQueryCollectionItems] Received items:", {
          leftCount: data.left.length,
          rightCount: data.right.length,
          totalCount: data.left.length + data.right.length,
          requestId: thisRequestId,
        })

        setAllLeftItems(data.left)
        setAllRightItems(data.right)
        setActiveRequestId(thisRequestId)

        await loadItemsBatch(data.left, data.right, 0, ITEMS_PER_LOAD, thisRequestId)
        
        if (thisRequestId === currentRequestIdRef.current) {
          setInitialLoadComplete(true)
        }
      } catch (err) {
        console.error("[useQueryCollectionItems] Error fetching IDs:", err)
        if (thisRequestId === currentRequestIdRef.current) {
          setError(err)
        }
      } finally {
        if (thisRequestId === currentRequestIdRef.current) {
          setLoading(false)
        }
      }
    }

    fetchIds()
  }, [collectionId, user, refetchTrigger])

  const loadItemsBatch = async (
    leftItems: TattooItemRef[],
    rightItems: TattooItemRef[],
    startIndex: number,
    count: number,
    requestId: number
  ) => {
    const itemsPerColumn = Math.ceil(count / 2)
    const leftBatch = leftItems.slice(startIndex, startIndex + itemsPerColumn)
    const rightBatch = rightItems.slice(startIndex, startIndex + itemsPerColumn)
    const allItemsToFetch = [...leftBatch, ...rightBatch]

    if (allItemsToFetch.length === 0) return

    const itemPromises = allItemsToFetch.map(async (itemRef) => {
      try {
        const collectionName = itemRef.type === "flash" ? "flashes" : "portfolioItems"
        const docRef = doc(db, collectionName, itemRef.id)
        const docSnap = await getDoc(docRef)
        
        if (!docSnap.exists()) {
          console.warn(`[useQueryCollectionItems] Document ${itemRef.id} not found in ${collectionName}`)
          return null
        }

        const data = docSnap.data()
        const item: PortfolioItem = {
          id: docSnap.id,
          artistId: data.artistId as string,
          type: (data.type as PortfolioItem["type"]) ?? "image",
          storagePath: (data.storagePath as string) ?? "",
          imageUrl: data.imageUrl as string,
          width: (data.width as number) ?? 0,
          height: (data.height as number) ?? 0,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
        }
        
        if (data.compressedImageUrl) item.compressedImageUrl = data.compressedImageUrl
        if (data.styles) item.styleTags = data.styles as string[]
        if (data.caption) item.caption = data.caption as string
        if (data.timedSaves) {
          item.timedSaves = data.timedSaves.map((saveData: any) => ({
            itemId: saveData.itemId,
            userId: saveData.userId,
            savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
          }))
        }

        if (itemRef.type === "flash") {
          item.isFlash = true
          if (data.minPrice) item.minPrice = data.minPrice
          if (data.minSize) item.minSize = data.minSize
          if (data.available !== undefined) item.available = data.available
          if (data.accessCode) item.accessCode = data.accessCode
          
          // Handle discount field
          if (data.discount) {
            item.discount = {
              absoluteAmount: data.discount.absoluteAmount,
              procentualAccount: data.discount.procentualAccount,
              newPrice: data.discount.newPrice,
              oldPrice: data.discount.oldPrice,
              discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
              discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
            }
          }
        }
        
        return item
      } catch (err) {
        console.error(`[useQueryCollectionItems] Error fetching document ${itemRef.id}:`, err)
        return null
      }
    })

    const fetchedItems = await Promise.all(itemPromises)
    const validItems = fetchedItems.filter((item): item is PortfolioItem => item !== null)

    if (requestId !== currentRequestIdRef.current) {
      return
    }

    const itemMap = new Map<string, PortfolioItem>()
    validItems.forEach(item => itemMap.set(item.id, item))

    const orderedItems: PortfolioItem[] = []
    const maxLength = Math.max(leftBatch.length, rightBatch.length)
    
    for (let i = 0; i < maxLength; i++) {
      if (i < leftBatch.length && itemMap.has(leftBatch[i].id)) {
        orderedItems.push(itemMap.get(leftBatch[i].id)!)
      }
      if (i < rightBatch.length && itemMap.has(rightBatch[i].id)) {
        orderedItems.push(itemMap.get(rightBatch[i].id)!)
      }
    }

    setLoadedItems(prev => [...prev, ...orderedItems])
    setCurrentLoadedCount(prev => prev + orderedItems.length)
  }

  const loadMoreItems = useCallback(async () => {
    if (loadingMore) return

    const totalAvailableItems = allLeftItems.length + allRightItems.length
    const hasMore = currentLoadedCount < totalAvailableItems

    if (!hasMore) {
      return
    }

    setLoadingMore(true)

    try {
      const itemsPerColumn = Math.ceil(currentLoadedCount / 2)
      await loadItemsBatch(allLeftItems, allRightItems, itemsPerColumn, ITEMS_PER_LOAD, activeRequestId)
    } catch (err) {
      console.error("[useQueryCollectionItems] Error loading more items:", err)
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, allLeftItems, allRightItems, currentLoadedCount, activeRequestId])

  const totalAvailableItems = allLeftItems.length + allRightItems.length
  const hasMore = currentLoadedCount < totalAvailableItems

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  return {
    items: loadedItems,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreItems,
    initialLoadComplete,
    refetch,
  }
}

